"use client";

/**
 * The crate-digging drawer (Gordon 2026-11-02: "pull drag down the header to
 * open the digging crates drawer"). Drag the header down (touch or mouse) —
 * or click the small handle under it, or ask coπ "dig the crates" — and a
 * full-screen drawer slides down with Releases, Playlists and (signed in)
 * Favorites as record crates, plus a search that narrows the crate.
 *
 * Mounted once in the root layout (not inside the header component), so the
 * header itself needs no change: a drag that STARTS inside <header> and pulls
 * down past 70px opens it. The drawer sits at z-[101] like EP-X focus mode, so
 * the chat button and coπ stay on top (stone rule). Esc, ✕ or a drag up closes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { ArrowLeft, Disc3, Heart, ListMusic, Search, X } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { getCrateReleases, getPopularPlaylists, type CrateRelease } from "@/services/home";
import { getFavorites } from "@/services/favorites";
import { getPlaylistTracks, getUserPlaylists } from "@/services/playlists";
import { CrateView, type CrateItem } from "@/components/crates/crate-view";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Tab = "releases" | "playlists" | "favorites";
const PULL_OPEN_PX = 70;

export function CrateDrawerHost() {
  const t = useTranslations("crates");
  const pathname = usePathname();
  const { user } = useAuth();
  const { playTracks } = useMediaPlayer();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("releases");
  const [q, setQ] = useState("");
  const [pull, setPull] = useState(0);
  const [data, setData] = useState<{ releases?: CrateItem[]; playlists?: CrateItem[]; favorites?: CrateItem[] }>({});
  const loading = useRef(new Set<Tab>());
  const hidden = !pathname || pathname.startsWith("/signin") || pathname.startsWith("/screens");
  // Gordon 2026-11-02: the drawer is everything between the header and the
  // player, the header its handle — pull it straight down, the drawer follows
  // the finger; push it back up to close. Every look (From above, Wide, Crate)
  // lives there ("flex between header and footer").
  const openStyle = true;
  const openStyleRef = useRef(openStyle);
  openStyleRef.current = openStyle;
  const pullRef = useRef(0);

  // Opening a record's page ("Open") or any navigation closes the drawer.
  useEffect(() => { setOpen(false); }, [pathname]);

  // ── Open from anywhere: bb-crates {open?, tab?} (coπ, buttons) ──
  useEffect(() => {
    const onCrates = (e: Event) => {
      const d = (e as CustomEvent<{ open?: boolean; tab?: Tab }>).detail || {};
      if (d.tab) setTab(d.tab);
      setOpen(o => (typeof d.open === "boolean" ? d.open : !o));
    };
    window.addEventListener("bb-crates", onCrates);
    return () => window.removeEventListener("bb-crates", onCrates);
  }, []);

  // ── Pull the header down (push it up to close the open drawer) ──
  // Mouse/pen through pointer events; fingers through touch events, because a
  // phone turns a vertical drag into a page scroll and CANCELS the pointer
  // stream after a few pixels — the drawer never reached 70 px and looked stuck
  // (Gordon 2026-11-02: "can't open the vinyl drawer on mobile").
  useEffect(() => {
    if (hidden) return;
    let start: { x: number; y: number } | null = null;
    let vertical = false;
    const setP = (v: number) => { pullRef.current = v; setPull(v); };
    const begin = (x: number, y: number, target: EventTarget | null) => {
      if (open && !openStyleRef.current) return false;
      if (!(target as Element | null)?.closest?.("header")) return false;
      start = { x, y }; vertical = false;
      return true;
    };
    /** Returns true when the drag is ours (the caller then stops the page scrolling). */
    const track = (x: number, y: number) => {
      if (!start) return false;
      const dy = y - start.y, dx = Math.abs(x - start.x);
      if (!vertical) {
        if (dx > Math.abs(dy)) { if (dx > 24) { start = null; setP(0); } return false; } // sideways = the favorites strip scrolling
        if (Math.abs(dy) < 6) return false;
        vertical = true;
      }
      if (open) { if (dy < -40) { start = null; setOpen(false); } return true; }
      setP(Math.max(0, Math.min(dy, window.innerHeight))); // the drawer follows the finger
      return true;
    };
    const finish = () => {
      if (start && !open && pullRef.current > PULL_OPEN_PX) setOpen(true);
      start = null; vertical = false; setP(0);
    };
    const down = (e: PointerEvent) => { if (e.isPrimary && e.pointerType !== "touch") begin(e.clientX, e.clientY, e.target); };
    const move = (e: PointerEvent) => { if (e.pointerType !== "touch") track(e.clientX, e.clientY); };
    const up = (e: PointerEvent) => { if (e.pointerType !== "touch") finish(); };
    const tStart = (e: TouchEvent) => { if (e.touches.length === 1) begin(e.touches[0].clientX, e.touches[0].clientY, e.target); };
    const tMove = (e: TouchEvent) => {
      if (!start || e.touches.length !== 1) return;
      if (track(e.touches[0].clientX, e.touches[0].clientY) && e.cancelable) e.preventDefault();
    };
    const tEnd = () => { if (start) finish(); };
    window.addEventListener("pointerdown", down);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    window.addEventListener("touchstart", tStart, { passive: true });
    window.addEventListener("touchmove", tMove, { passive: false });
    window.addEventListener("touchend", tEnd);
    window.addEventListener("touchcancel", tEnd);
    return () => {
      window.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      window.removeEventListener("touchstart", tStart);
      window.removeEventListener("touchmove", tMove);
      window.removeEventListener("touchend", tEnd);
      window.removeEventListener("touchcancel", tEnd);
    };
  }, [hidden, open]);

  // ── Esc: out of an opened release/playlist first, then close; lock page scroll while open ──
  const backRef = useRef<() => boolean>(() => false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !backRef.current()) setOpen(false); };
    window.addEventListener("keydown", onKey);
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.documentElement.style.overflow = prev; };
  }, [open]);

  // A playlist opened inside the drawer: its tracks become the crate
  // (Gordon 2026-11-02: "added tracks to this playlist … can't see the songs").
  const [dig, setDig] = useState<{ id: string; title: string; items: CrateItem[] } | null>(null);
  useEffect(() => { setDig(null); }, [tab, open]);
  backRef.current = () => { if (!dig) return false; setDig(null); return true; };
  const playlistTracks = useCallback(async (id: string) => {
    const raw: any[] = user
      ? await getPlaylistTracks(id)
      : await fetch(`/api/playlists/${encodeURIComponent(id)}/tracks`).then(r => (r.ok ? r.json() : { tracks: [] })).then(j => j.tracks || []);
    return raw.filter(t => t.audioUrl).map(t => ({ id: String(t.trackId || t.docId), title: t.title || "", artist: t.artist || "", imageUrl: t.imageUrl || "", audioUrl: t.audioUrl, mediaType: "audio" }) as any);
  }, [user]);
  const openPlaylist = useCallback(async (id: string, title: string) => {
    const tracks = await playlistTracks(id).catch(() => []);
    setDig({
      id, title,
      items: tracks.map((tr: any, i: number) => ({
        id: `${i}-${tr.id}`, title: tr.title, subtitle: tr.artist, imageUrl: tr.imageUrl, href: `/music/${tr.id}`,
        onPlay: () => playTracks(tracks, i),
      })),
    });
  }, [playlistTracks, playTracks]);

  // ── Load each crate the first time it's opened ──
  const playRelease = useCallback((r: CrateRelease, from = 0) => {
    if (!r.tracks.length) return;
    playTracks(r.tracks.map(tr => ({ id: tr.id, title: tr.title, artist: r.artist, imageUrl: r.imageUrl, audioUrl: tr.audioUrl, mediaType: "audio" }) as any), from);
  }, [playTracks]);
  // A release opened inside the drawer: its tracks become the crate, like a
  // playlist (Gordon 2026-11-02: "open a release and scroll through the tracks").
  const openRelease = useCallback((r: CrateRelease) => {
    setDig({
      id: r.id, title: r.title,
      items: r.tracks.map((tr, i) => ({
        id: `${i}-${tr.id}`, title: tr.title, subtitle: r.artist, imageUrl: r.imageUrl, href: `/music/${tr.id}`,
        onPlay: () => playRelease(r, i),
      })),
    });
  }, [playRelease]);

  useEffect(() => {
    if (!open || data[tab] || loading.current.has(tab)) return;
    loading.current.add(tab);
    const load = async (): Promise<CrateItem[]> => {
      if (tab === "releases") {
        return (await getCrateReleases()).map(r => ({
          id: r.id, title: r.title, subtitle: r.artist, imageUrl: r.imageUrl, href: `/releases/${r.id}`,
          onPlay: r.tracks.length ? () => playRelease(r) : undefined,
          onOpen: r.tracks.length ? () => openRelease(r) : undefined,
        }));
      }
      if (tab === "playlists") {
        // Your own playlists first (private ones too), then the public ones.
        const own: any[] = user ? await getUserPlaylists(user.uid).catch(() => []) : [];
        const pub: any[] = await getPopularPlaylists(300).catch(() => []);
        const seen = new Set<string>();
        return [...own, ...pub].filter(p => !seen.has(p.id) && seen.add(p.id)).map(p => ({
          id: p.id, title: p.title || "", subtitle: p.creatorName || p.creator || "",
          imageUrl: p.coverImageUrl || (p.coverImages || [])[0] || p.imageUrl || "",
          href: `/playlist/${p.id}`,
          onPlay: () => { void playlistTracks(p.id).then(tr => { if (tr.length) playTracks(tr, 0); }); },
          onOpen: () => { void openPlaylist(p.id, p.title || ""); },
        }));
      }
      if (!user) return [];
      return (await getFavorites(user.uid)).map(f => ({
        id: f.id, title: f.title, subtitle: f.subtitle, imageUrl: f.imageUrl, href: f.link,
        onPlay: f.audioUrl ? () => playTracks([{ id: f.itemId, title: f.title, artist: f.subtitle || "", imageUrl: f.imageUrl || "", audioUrl: f.audioUrl!, mediaType: "audio" } as any], 0) : undefined,
      }));
    };
    load().then(items => setData(d => ({ ...d, [tab]: items }))).catch(() => setData(d => ({ ...d, [tab]: [] }))).finally(() => loading.current.delete(tab));
  }, [open, tab, data, user, playRelease, openRelease, playTracks, playlistTracks, openPlaylist]);

  const items = useMemo(() => {
    const list = dig ? dig.items : data[tab] || [];
    const needle = q.trim().toLowerCase();
    return needle ? list.filter(i => `${i.title} ${i.subtitle || ""}`.toLowerCase().includes(needle)) : list;
  }, [data, tab, q, dig]);

  if (hidden) return null;

  const tabs: { key: Tab; icon: typeof Disc3 }[] = [
    { key: "releases", icon: Disc3 },
    { key: "playlists", icon: ListMusic },
    ...(user ? [{ key: "favorites" as Tab, icon: Heart }] : []),
  ];

  return (
    <>
      {/* The handle under the header: grows while you pull; click works too. */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t("openDrawer")}
          title={t("openDrawer")}
          className="fixed left-1/2 top-[calc(var(--header-height)_-_10px)] z-[60] flex h-6 w-20 -translate-x-1/2 items-center justify-center"
        >
          <span className="block h-1.5 rounded-full bg-foreground/50 transition-all duration-150" style={{ width: `${32 + pull * 0.4}px`, transform: `translateY(${pull * 0.35}px)` }} />
        </button>
      )}

      {/* Open style: the drawer lives under the header (z below the player bar
          and chat button) and slides out from behind it. Box style: full screen. */}
      <div className={cn(openStyle && "pointer-events-none fixed inset-x-0 bottom-0 top-[var(--header-height)] z-[99] overflow-hidden")}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("title")}
        inert={!open}
        className={cn(
          "flex flex-col bg-background ease-out motion-reduce:transition-none",
          openStyle ? "pointer-events-auto absolute inset-0 bb-drawer-shadow" : "fixed inset-0 z-[101]",
          pull > 0 ? "transition-none" : "transition-transform duration-300",
          !(openStyle && pull > 0) && (open ? "translate-y-0" : "-translate-y-full"),
        )}
        style={openStyle && pull > 0 && !open ? { transform: `translateY(calc(-100% + ${pull}px))` } : undefined}
      >
        {/* One row: title, the crate tabs, search, close (Gordon 2026-11-02:
            "move Releases / Playlists / Favorites up one row"). Phones: the
            tabs drop below. Close sits under the menu button, so it leads the
            row when the menu is on the left. */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-page py-2">
          <Disc3 className="h-5 w-5 shrink-0 max-sm:order-1 menuright:max-sm:order-none" aria-hidden="true" />
          <h2 className="hidden shrink-0 text-base font-bold sm:block lg:text-lg">{t("title")}</h2>
          {/* Phones: icon + search + close on the first row, the tabs on the next (Gordon 2026-11-02). */}
          {/* Phones: the three tabs share the full width (Gordon 2026-11-03). */}
          <div className="order-last flex w-full min-w-0 gap-1 sm:order-none sm:w-auto" role="tablist" aria-label={t("title")}>
            {tabs.map(({ key, icon: Icon }) => (
              <Button key={key} type="button" role="tab" aria-selected={tab === key} size="sm" variant={tab === key ? "default" : "outlineContrast"} className="h-9 min-w-0 flex-1 gap-1.5 px-2 sm:flex-none sm:px-3" onClick={() => { if (tab === key) setDig(null); else setTab(key); }}>
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /><span className="truncate">{t(`tab.${key}`)}</span>
              </Button>
            ))}
          </div>
          <label className="relative min-w-0 flex-1 sm:ml-auto sm:w-56 sm:flex-none">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder={t("search")} aria-label={t("search")} className="h-9 pl-8" />
          </label>
          {/* Close: outlined, centred right under the menu button (Gordon
              2026-11-03) — the menu button is a header-height square in the
              corner on the menu side, so the 36px button sits in that column. */}
          <Button
            type="button"
            variant="outlineContrast"
            size="icon"
            onClick={() => setOpen(false)}
            aria-label={t("close")}
            className="order-first h-9 w-9 shrink-0 ml-[calc((var(--header-height)_-_2.25rem)_/_2_-_var(--page-gutter))] menuright:order-none menuright:ml-0 menuright:mr-[calc((var(--header-height)_-_2.25rem)_/_2_-_var(--page-gutter))]"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        {/* Inside a playlist or a release: its name and the way back. */}
        {dig && (
          <div className="flex items-center gap-2 px-page pt-2">
            <Button type="button" variant="outlineContrast" size="sm" className="h-9 gap-1.5" onClick={() => setDig(null)}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />{t(`back.${tab}`)}
            </Button>
            <p className="min-w-0 truncate text-base font-bold">{dig.title}</p>
          </div>
        )}
        <div className={cn("flex min-h-0 flex-1 items-center justify-center overflow-hidden px-page pb-[calc(var(--player-height,56px)_+_16px)]", openStyle ? "pt-2" : "pt-4")}>
          {open && (data[tab]
            ? <CrateView key={`${tab}-${dig?.id || ""}-${q}`} items={items} label={dig ? dig.title : t(`tab.${tab}`)} fill={openStyle} back={dig ? { label: t(`back.${tab}`), onBack: () => setDig(null) } : undefined} />
            : <p className="text-muted-foreground">{t("loading")}</p>)}
        </div>
      </div>
      </div>
    </>
  );
}
